import { useTranslation } from '@jq4race/i18n';
import { CloudUpload, WifiOff } from 'lucide-react';

import type { Identity } from '../../auth/domain/resolve-identity';
import { usePendingSyncCount } from '../../offline/ui/use-pending-sync-count';
import { useOnlineStatus } from '../hooks/use-online-status';

interface ConnectivityBadgeProps {
  /**
   * pwa-offline-first Slice 6 (coordinator finding #4) — resolved once by
   * `_authenticated.tsx` (the only place with both `useAuthState()` and the
   * route's `offlineProfile` in scope) and threaded down via `AppShell`, so
   * this component never needs router/auth context itself and stays
   * trivially testable. `null` while identity hasn't resolved yet — the
   * pending count reads 0 in that case (see `usePendingSyncCount`).
   */
  identity: Identity | null;
}

/**
 * Online/offline indicator + pending-sync count (design §5, task 6.8).
 * Renders nothing when there is nothing to say: online AND zero unsynced
 * items — the common case, kept clutter-free. The pending chip is
 * DELIBERATELY shown even while online: outbox-always (decision #12) means
 * an item can sit unsynced for a moment even with connectivity (before
 * Slice 7's SyncEngine drains it), and the operator should never wonder
 * where a just-queued check-in went.
 */
export function ConnectivityBadge({ identity }: ConnectivityBadgeProps) {
  const { t } = useTranslation('shell');
  const isOnline = useOnlineStatus();
  const pendingCount = usePendingSyncCount(identity, { includeRejected: false });

  if (isOnline && pendingCount === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      {!isOnline && (
        <div className="flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-1 text-destructive">
          <WifiOff className="size-3.5" strokeWidth={2} aria-hidden />
          <span className="text-[11px] font-semibold uppercase tracking-wide">
            {t('connectivity.offline')}
          </span>
        </div>
      )}
      {pendingCount > 0 && (
        <div
          role="status"
          className="flex items-center gap-1 rounded-full bg-accent/15 px-2 py-1 text-jq-gold-bright"
        >
          <CloudUpload className="size-3.5" strokeWidth={2} aria-hidden />
          <span className="text-[11px] font-semibold tabular-nums">{pendingCount}</span>
        </div>
      )}
    </div>
  );
}
